/* ============================================================================
 * sp-variant-picker.js — Sportiva .925
 * ----------------------------------------------------------------------------
 * Selector de variantes portable (Dawn + Horizon), sin dependencias.
 *
 * ESTRATEGIA (híbrida, 2 fases):
 *   Fase 1 — Optimista (0 ms):  al cambiar una opción, se actualiza precio,
 *            SKU, disponibilidad y galería desde un JSON *slim* embebido.
 *            El usuario ve el cambio inmediatamente.
 *   Fase 2 — Reconciliación (~120 ms debounce): se pide al servidor el mismo
 *            section con `?variant=<id>&section_id=<id>` (Section Rendering
 *            API) y se reemplaza SOLO el innerHTML de los nodos marcados con
 *            [data-sp-refresh]. Esto trae la verdad del servidor: descuentos,
 *            unit price, mensajes de inventario, moneda del Market activo y
 *            el botón de pago dinámico.
 *
 * Por qué híbrido y no solo Section Rendering: un fetch por click se siente
 * lento en 4G. Por qué no solo cliente: el precio con Markets/descuentos
 * automáticos NO se puede calcular confiablemente en JS.
 *
 * REGLA CRÍTICA: <sp-variant-picker> NUNCA debe estar dentro de un nodo
 * [data-sp-refresh]. Si se reemplaza a sí mismo pierde el foco del teclado,
 * el estado de los radios y los listeners. Ver README.
 *
 * Eventos emitidos (hook para GA4 / Meta Pixel / Salesforce):
 *   'sp:variant:change'  detail: { variant, productId, sectionId }
 *   'sp:variant:missing' detail: { values, productId }   -> combinación inexistente
 * ========================================================================== */

/* ---------------------------------------------------------------------------
 * 1) Adaptador de galería
 * ---------------------------------------------------------------------------
 * Único punto del código acoplado al tema. Se resuelve UNA vez por instancia.
 * Orden de detección: Dawn -> genérico por data-media-id -> no-op.
 * Añadir un tema nuevo = añadir una estrategia aquí, nada más.
 * ------------------------------------------------------------------------- */
const SPMediaAdapter = {
  resolve(sectionId) {
    // --- Dawn (y derivados): <media-gallery> expone setActiveMedia() ---
    const gallery = document.querySelector('media-gallery');
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      return (mediaId) => {
        // Dawn indexa por `${sectionId}-${mediaId}`.
        gallery.setActiveMedia(`${gallery.dataset.section || sectionId}-${mediaId}`, true);
      };
    }

    // --- Horizon / temas genéricos: se activa el thumbnail correspondiente ---
    return (mediaId) => {
      const node = document.querySelector(
        `[data-media-id="${mediaId}"], [data-media-id$="-${mediaId}"]`
      );
      if (!node) return;
      const trigger = node.matches('button, a') ? node : node.querySelector('button, a');
      (trigger || node).click();
    };
  },
};

/* ---------------------------------------------------------------------------
 * 2) Componente
 * ------------------------------------------------------------------------- */
class SPVariantPicker extends HTMLElement {
  #abort = null;      // AbortController del fetch en vuelo
  #timer = 0;         // id del debounce
  #setMedia = null;   // estrategia de galería resuelta
  #lastMediaId = 0;   // evita re-disparar la galería con el mismo medio

  connectedCallback() {
    const payload = this.querySelector('[data-sp-variants]');
    if (!payload) {
      console.warn('[sp-variant-picker] falta el bloque [data-sp-variants]');
      return;
    }

    /** @type {Array<{id:number,options:string[],available:boolean,price:number,
     *                price_formatted:string,compare_at_price:number,
     *                compare_at_formatted:string,sku:string,
     *                featured_media_id:number,inventory_quantity:number}>} */
    this.variants = JSON.parse(payload.textContent);

    this.productId = this.dataset.productId;
    this.productUrl = this.dataset.productUrl;
    this.sectionId = this.dataset.sectionId;
    this.shouldUpdateUrl = this.dataset.updateUrl !== 'false';

    this.#setMedia = SPMediaAdapter.resolve(this.sectionId);

    // Un solo listener delegado en el host: O(1) listeners sin importar
    // cuántas opciones tenga el producto.
    this.addEventListener('change', this.#onChange);

    // Pinta estados (agotado / inexistente) en el primer render.
    this.#paintAvailability();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#abort?.abort();
    this.removeEventListener('change', this.#onChange);
  }

  /* ------------------------------------------------------------------ *
   * Lectura de estado
   * ------------------------------------------------------------------ */

  /** Grupos de opción ordenados por posición (0 = primera opción). */
  get optionGroups() {
    return Array.from(this.querySelectorAll('[data-sp-option-group]')).sort(
      (a, b) => Number(a.dataset.spOptionGroup) - Number(b.dataset.spOptionGroup)
    );
  }

  /** Valores seleccionados, alineados con `variant.options` de Shopify. */
  get selectedValues() {
    return this.optionGroups.map((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) return checked.value;
      const select = group.querySelector('select');
      return select ? select.value : null;
    });
  }

  /** Busca la variante exacta. O(n·k) con n = variantes, k = opciones. */
  #findVariant(values) {
    return (
      this.variants.find((v) => v.options.every((opt, i) => opt === values[i])) || null
    );
  }

  /* ------------------------------------------------------------------ *
   * Handler principal
   * ------------------------------------------------------------------ */
  #onChange = (event) => {
    if (!event.target.matches('[data-sp-option-input]')) return;

    // Si el cambio invalidó una opción posterior, se autocorrige.
    this.#reconcileSelection(Number(event.target.closest('[data-sp-option-group]').dataset.spOptionGroup));

    const values = this.selectedValues;
    const variant = this.#findVariant(values);

    this.#paintAvailability();
    this.#paintLegends(values);
    this.#applyOptimistic(variant);
    this.#syncForms(variant);
    this.#syncUrl(variant);

    this.dispatchEvent(
      new CustomEvent(variant ? 'sp:variant:change' : 'sp:variant:missing', {
        bubbles: true,
        detail: { variant, values, productId: this.productId, sectionId: this.sectionId },
      })
    );

    this.#scheduleServerRender(variant);
  };

  /* ------------------------------------------------------------------ *
   * Disponibilidad en cascada
   * ------------------------------------------------------------------ *
   * Para la opción en posición `p`, el universo de variantes se restringe
   * a las que ya coinciden en las posiciones 0..p-1. Sobre ese subconjunto:
   *   - no existe ninguna  -> 'unavailable' (se deshabilita el input)
   *   - existe pero 0 stock -> 'sold-out'   (clicable, muestra tachado)
   *   - existe y hay stock  -> 'available'
   * Distinguir los dos primeros es lo que Dawn NO hace por defecto y es
   * crítico aquí: la mayoría de las combinaciones color × material de
   * Sportiva están en 0.
   * ------------------------------------------------------------------ */
  #paintAvailability() {
    const selected = this.selectedValues;

    this.optionGroups.forEach((group, position) => {
      const pool = this.variants.filter((v) =>
        selected.slice(0, position).every((val, i) => v.options[i] === val)
      );

      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const matches = pool.filter((v) => v.options[position] === input.value);
        const exists = matches.length > 0;
        const available = matches.some((v) => v.available);

        input.disabled = !exists;
        const wrapper = input.closest('[data-sp-option-value]');
        if (wrapper) {
          wrapper.dataset.spState = !exists ? 'unavailable' : available ? 'available' : 'sold-out';
        }
      });
    });
  }

  /**
   * Tras cambiar la opción `changedPosition`, las posteriores pueden haber
   * quedado en un valor inexistente. Se mueve cada una al primer valor
   * disponible (o al primero existente si ninguno tiene stock).
   */
  #reconcileSelection(changedPosition) {
    const groups = this.optionGroups;

    for (let p = changedPosition + 1; p < groups.length; p++) {
      const selected = this.selectedValues;
      const pool = this.variants.filter((v) =>
        selected.slice(0, p).every((val, i) => v.options[i] === val)
      );
      if (!pool.length) break;

      const current = selected[p];
      if (pool.some((v) => v.options[p] === current)) continue; // sigue siendo válido

      const preferred = (pool.find((v) => v.available) || pool[0]).options[p];
      const input = groups[p].querySelector(`input[value="${CSS.escape(preferred)}"]`);
      if (input) input.checked = true;
    }
  }

  /** Actualiza el texto "Color: Azul Marino" junto al <legend>. */
  #paintLegends(values) {
    this.querySelectorAll('[data-sp-selected-label]').forEach((node) => {
      const idx = Number(node.dataset.spSelectedLabel);
      node.textContent = values[idx] ?? '';
    });
  }

  /* ------------------------------------------------------------------ *
   * Fase 1 — pintado optimista
   * ------------------------------------------------------------------ */
  #applyOptimistic(variant) {
    const scope = document;

    const price = scope.querySelector('[data-sp-price]');
    const compare = scope.querySelector('[data-sp-compare]');
    const sku = scope.querySelector('[data-sp-sku]');
    const stock = scope.querySelector('[data-sp-stock]');

    if (!variant) {
      if (price) price.textContent = '—';
      if (compare) compare.hidden = true;
      if (stock) stock.textContent = 'Combinación no disponible';
      return;
    }

    if (price) price.textContent = variant.price_formatted;

    if (compare) {
      const onSale = variant.compare_at_price > variant.price;
      compare.hidden = !onSale;
      if (onSale) compare.textContent = variant.compare_at_formatted;
    }

    if (sku) {
      sku.textContent = variant.sku || '';
      sku.hidden = !variant.sku;
    }

    if (stock) {
      stock.textContent = variant.available
        ? variant.inventory_quantity > 0 && variant.inventory_quantity <= 3
          ? `Últimas ${variant.inventory_quantity} piezas`
          : 'Disponible'
        : 'Agotado — hecho a pedido en Taxco';
      stock.dataset.spStockState = variant.available ? 'in' : 'out';
    }

    // Galería: solo si la variante trae media propia y es distinta a la actual.
    if (variant.featured_media_id && variant.featured_media_id !== this.#lastMediaId) {
      this.#lastMediaId = variant.featured_media_id;
      this.#setMedia(variant.featured_media_id);
    }
  }

  /**
   * Mantiene sincronizado el input oculto [name="id"] de TODOS los formularios
   * de carrito de la página (producto principal + sticky bar + quick-add).
   * `form[action*="/cart/add"]` es la única selección realmente portable:
   * no depende de clases ni de nombres de sección del tema.
   */
  #syncForms(variant) {
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      const input = form.querySelector('input[name="id"], select[name="id"]');
      if (input) {
        input.value = variant ? variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const submit = form.querySelector('[type="submit"]');
      if (!submit) return;
      const buyable = Boolean(variant && variant.available);
      submit.disabled = !buyable;
      submit.setAttribute('aria-disabled', String(!buyable));
      const text = submit.querySelector('span') || submit;
      text.textContent = !variant
        ? 'No disponible'
        : variant.available
          ? 'Agregar al carrito'
          : 'Agotado';
    });
  }

  /** replaceState (no pushState): no ensucia el historial con cada click. */
  #syncUrl(variant) {
    if (!this.shouldUpdateUrl || !variant) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variant: variant.id }, '', url.toString());
  }

  /* ------------------------------------------------------------------ *
   * Fase 2 — reconciliación con el servidor
   * ------------------------------------------------------------------ */
  #scheduleServerRender(variant) {
    if (!variant || !this.sectionId) return;
    clearTimeout(this.#timer);
    // 120 ms: colapsa ráfagas de clicks (usuario explorando colores) en
    // una sola petición. Sin esto, 6 clicks = 6 fetches.
    this.#timer = window.setTimeout(() => this.#renderFromServer(variant), 120);
  }

  async #renderFromServer(variant) {
    this.#abort?.abort();              // cancela la petición anterior en vuelo
    this.#abort = new AbortController();

    const url = `${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`;
    this.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(url, { signal: this.#abort.signal });
      if (!res.ok) throw new Error(`Section Rendering API: HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      document.querySelectorAll('[data-sp-refresh]').forEach((node) => {
        const fresh = doc.querySelector(`[data-sp-refresh="${node.dataset.spRefresh}"]`);
        if (fresh && fresh.innerHTML !== node.innerHTML) {
          node.innerHTML = fresh.innerHTML;
        }
      });

      // El botón de pago dinámico (Shop Pay / GPay) se reinyecta al
      // re-renderizar buy-buttons; Shopify lo reinicializa vía este evento.
      document.dispatchEvent(new CustomEvent('shopify:section:load', {
        detail: { sectionId: this.sectionId },
      }));
    } catch (err) {
      if (err.name !== 'AbortError') {
        // Falla suave: la fase optimista ya dejó la UI en un estado correcto.
        console.warn('[sp-variant-picker]', err);
      }
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('sp-variant-picker')) {
  customElements.define('sp-variant-picker', SPVariantPicker);
}

export { SPVariantPicker, SPMediaAdapter };
